import { useEffect, useMemo, useRef, useState } from 'react'
import { ScrollView } from 'react-native-gesture-handler'
import { Text, TextInput } from 'react-native-paper'
import { TouchableIconWithToolTip } from '../../../utils/meldd-tooltip'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import {
  NodeTierConfig,
  TierConfigDto,
  useGetNodeTierConfigQuery,
  useCancelNodeTierMutation,
  useReactivateNodeTierMutation,
} from '../../../generated/graphql'
import { Dimensions, View, Linking } from 'react-native'
import { useCurrency } from './payment.utils'
import { useIsMobileDevice } from '../../../hooks/is-mobile.hook'
import { Icon, MelddButton, Row } from '../../../@ui'
import { NodeTierStatusBadge } from './components/node-tier-status.badge'
import { UpgradeNodeTierButton } from './components/upgrade-node-tier.button'

import { useTheme } from '../../../hooks/theme.hook'
import { createStyle } from '../../../contexts/global-styles.provider'
import { MelddRawCard } from '../../../@ui/containers/cards/meldd-raw.card'
import {
  ConfirmDialogue,
  ConfirmDialogueRef,
} from '../../../@ui/containers/modals/confirm.dialogue'
import Toast from 'react-native-root-toast'
import * as Sentry from '@sentry/react-native'
import { MessageType } from '../../../utils/message-type'
import { DowngradeNodeTierButton } from './components/downgrade-node-tier.button'
import { CancelDowngradeButton } from './components/cancel-downgrade.button'

const NodeTierCard = ({
  children,
  title,
  action,
  currentTier,
  tierName,
  selectedSeats,
  pendingTier,
}: {
  children: React.ReactNode
  title: string
  action?: React.ReactNode
  currentTier?: {
    name: string
    seats: number
    id?: string
    endDate?: string
    status?: string
  }
  tierName?: string
  selectedSeats?: number
  pendingTier?: {
    id: string
    name: string
    seats: number
    status: string
    startDate?: string | null
    endDate?: string | null
  }
}) => {
  const { t } = useTranslation('tiers')
  const styles = useStyle()
  const theme = useTheme()

  const isActive = useMemo(() => {
    if (!tierName || selectedSeats === undefined) {
      return false
    }
    return (
      currentTier?.name === tierName && currentTier?.seats === selectedSeats
    )
  }, [currentTier, tierName, selectedSeats])

  const isCancelled = useMemo(() => {
    return currentTier?.status === 'cancelled'
  }, [currentTier?.status])

  const hasPendingDowngrade = useMemo(() => {
    if (!pendingTier || !currentTier) {
      return false
    }
    // Check if there's a pending tier with status 'pending_renovation' and fewer seats
    return (
      pendingTier.status === 'pending_renovation' &&
      pendingTier.seats < currentTier.seats &&
      pendingTier.name === currentTier.name
    )
  }, [pendingTier, currentTier])

  // Check if this tier (not necessarily active) has a downgrade planned
  const hasDowngradePlannedForThisTier = useMemo(() => {
    if (!pendingTier || !tierName) {
      return false
    }
    return (
      pendingTier.status === 'pending_renovation' &&
      pendingTier.name === tierName
    )
  }, [pendingTier, tierName])

  const renovationDateInfo = useMemo(() => {
    if (!currentTier?.endDate || !isActive || isCancelled) {
      return {
        day: undefined,
        month: undefined,
        monthName: undefined,
        isDowngrade: false,
        pendingSeats: undefined,
      }
    }
    // Get the first day of next month (renovation happens on the 1st)
    const endDate = moment(currentTier.endDate)
    const nextMonth = endDate.clone().add(1, 'month').startOf('month')
    return {
      day: nextMonth.date(),
      month: nextMonth.month() + 1, // moment months are 0-indexed
      monthName: nextMonth.format('MMMM'), // Full month name for translation
      isDowngrade: hasPendingDowngrade,
      pendingSeats: pendingTier?.seats,
    }
  }, [currentTier, isActive, isCancelled, hasPendingDowngrade, pendingTier])

  const borderColor = isCancelled
    ? theme.colors.error
    : isActive
    ? theme.colors.primary
    : undefined

  return (
    <View
      style={
        borderColor
          ? {
              borderWidth: 2,
              borderColor: borderColor,
              borderRadius: 32,
              overflow: 'hidden',
            }
          : undefined
      }
    >
      <MelddRawCard
        height={500}
        header={
          <View>
            <Row
              flex={1}
              columnGap={4}
              justifyContent="space-between"
              style={styles.cardPadding}
            >
              <Text variant="titleMedium">{title}</Text>
              {currentTier && <NodeTierStatusBadge activeTier={currentTier} />}
            </Row>
          </View>
        }
        action={action}
      >
        <View style={styles.cardPadding}>
          {children}
          {isCancelled && (
            <View style={styles.renovationDateContainer}>
              <Text variant="bodySmall" style={styles.cancelledMessageText}>
                {t('tier-cancelled-message')}
              </Text>
            </View>
          )}
          {isActive &&
            !isCancelled &&
            renovationDateInfo.day &&
            renovationDateInfo.monthName && (
              <View style={styles.renovationDateContainer}>
                <Text variant="bodySmall" style={styles.renovationDateText}>
                  {renovationDateInfo.isDowngrade
                    ? t('will-be-downgrade-next-month', {
                        month: renovationDateInfo.monthName.toLowerCase(),
                        seats:
                          renovationDateInfo.pendingSeats === -1
                            ? t('unlimited')
                            : renovationDateInfo.pendingSeats?.toString() || '',
                      })
                    : t('will-be-automatically-renovate', {
                        day: renovationDateInfo.day,
                        month: renovationDateInfo.monthName.toLowerCase(),
                      })}
                </Text>
              </View>
            )}
          {!isActive &&
            !isCancelled &&
            hasDowngradePlannedForThisTier &&
            pendingTier && (
              <View style={styles.renovationDateContainer}>
                <Text variant="bodySmall" style={styles.renovationDateText}>
                  {t('already-plan-downgrade-planned', {
                    seats:
                      pendingTier.seats === -1
                        ? t('unlimited')
                        : pendingTier.seats.toString(),
                  })}
                </Text>
              </View>
            )}
        </View>
      </MelddRawCard>
    </View>
  )
}

const CancelNodeTierButton = ({
  nodeTierId,
  onCancelSuccess,
}: {
  nodeTierId: string
  onCancelSuccess?: () => void
}) => {
  const { t } = useTranslation('tiers')
  const confirmDialogRef = useRef<ConfirmDialogueRef>(null)
  const [cancelNodeTier, { loading }] = useCancelNodeTierMutation()

  const handleCancel = async () => {
    try {
      const result = await cancelNodeTier({
        variables: { id: nodeTierId },
      })

      if (result.data?.cancelNodeTier) {
        Toast.show(t('node-tier-cancelled'), MessageType.info)
        onCancelSuccess?.()
      }
    } catch (error) {
      console.error('Error cancelling node tier:', error)
      Sentry.captureException(error)
      Toast.show(t('error-cancelling-node-tier'), MessageType.error)
      return false
    }
  }

  return (
    <>
      <MelddButton
        onPress={() => confirmDialogRef.current?.open()}
        title={t('cancel')}
      />
      <ConfirmDialogue
        ref={confirmDialogRef}
        label={t('cancel-node-tier-title')}
        onContinue={handleCancel}
        acceptLabel={t('cancel')}
        cancelLabel={t('keep')}
      >
        <Text>{t('cancel-node-tier-message')}</Text>
      </ConfirmDialogue>
    </>
  )
}

const ReactivateNodeTierButton = ({
  nodeTierId,
  onReactivateSuccess,
}: {
  nodeTierId: string
  onReactivateSuccess?: () => void
}) => {
  const { t } = useTranslation('tiers')
  const confirmDialogRef = useRef<ConfirmDialogueRef>(null)
  const [reactivateNodeTier, { loading }] = useReactivateNodeTierMutation()

  const handleReactivate = async () => {
    try {
      const result = await reactivateNodeTier({
        variables: { id: nodeTierId },
      })

      if (result.data?.reactivateNodeTier) {
        Toast.show(t('node-tier-reactivated'), MessageType.info)
        onReactivateSuccess?.()
      }
    } catch (error) {
      console.error('Error reactivating node tier:', error)
      Sentry.captureException(error)
      Toast.show(t('error-reactivating-node-tier'), MessageType.error)
      return false
    }
  }

  return (
    <>
      <MelddButton
        onPress={() => confirmDialogRef.current?.open()}
        title={t('reactivate')}
      />
      <ConfirmDialogue
        ref={confirmDialogRef}
        label={t('reactivate-node-tier-title')}
        onContinue={handleReactivate}
        acceptLabel={t('reactivate')}
        cancelLabel={t('cancel')}
      >
        <Text>{t('reactivate-node-tier-message')}</Text>
      </ConfirmDialogue>
    </>
  )
}

const TierAction = ({
  tier,
  seats,
  currentTier,
  pendingTier,
  action,
  onCancelSuccess,
  onUpgradeSuccess,
  allowTierChanges = true,
}: {
  tier: TierConfigDto
  seats: number
  currentTier?: {
    name: string
    seats: number
    id?: string
    endDate?: string
    status?: string
  }
  pendingTier?: {
    id: string
    name: string
    seats: number
    status: string
    startDate?: string | null
    endDate?: string | null
  }
  action: (action: 'start' | 'upgrade' | 'downgrade') => void
  onCancelSuccess?: () => void
  onUpgradeSuccess?: (
    billingId: string,
    amount: number,
    basePrice?: number
  ) => void
  allowTierChanges?: boolean
}) => {
  const { t } = useTranslation('tiers')

  if (!allowTierChanges) {
    return (
      <Text
        variant="bodySmall"
        style={{
          opacity: 0.7,
          textAlign: 'right',
          flexShrink: 1,
          maxWidth: 220,
        }}
      >
        {t('tier-actions-owner-only')}
      </Text>
    )
  }

  // Check if there's a pending downgrade
  const hasPendingDowngrade =
    pendingTier &&
    currentTier &&
    pendingTier.status === 'pending_renovation' &&
    pendingTier.seats < currentTier.seats

  if (!currentTier) {
    return <MelddButton onPress={() => action('start')} title={t('start')} />
  }

  // If on same tier, compare seats
  if (currentTier.seats < seats) {
    if (currentTier.id) {
      return (
        <UpgradeNodeTierButton
          nodeTierId={currentTier.id}
          seats={seats}
          onUpgradeSuccess={onUpgradeSuccess}
        />
      )
    }
    return (
      <MelddButton onPress={() => action('upgrade')} title={t('upgrade')} />
    )
  }

  // If on same tier, compare seats
  if (currentTier.seats > seats) {
    if (currentTier.id) {
      return (
        <DowngradeNodeTierButton
          nodeTierId={currentTier.id}
          seats={seats}
          disabled={hasPendingDowngrade}
          onDowngradeSuccess={onCancelSuccess}
        />
      )
    }
    return (
      <MelddButton
        onPress={() => action('downgrade')}
        title={t('downgrade')}
        isDisabled={hasPendingDowngrade}
      />
    )
  }

  // Check if tier is cancelled
  if (currentTier.status === 'cancelled' && currentTier.id) {
    // If cancelled tier matches current tier and seats, show reactivate
    if (currentTier.name === tier.name && currentTier.seats === seats) {
      return (
        <ReactivateNodeTierButton
          nodeTierId={currentTier.id}
          onReactivateSuccess={onCancelSuccess}
        />
      )
    }
    // If cancelled but different tier/seats, show start/upgrade/downgrade as normal
  }

  if (currentTier.seats == seats && currentTier.id) {
    // Check if there's a pending downgrade (pending tier with fewer seats)
    const hasPendingDowngrade =
      pendingTier &&
      pendingTier.status === 'pending_renovation' &&
      pendingTier.seats < currentTier.seats

    if (hasPendingDowngrade && pendingTier.id) {
      return (
        <CancelDowngradeButton
          nodeTierId={currentTier.id}
          onCancelSuccess={onCancelSuccess}
        />
      )
    }
    return (
      <CancelNodeTierButton
        nodeTierId={currentTier.id}
        onCancelSuccess={onCancelSuccess}
      />
    )
  }

  if (currentTier.id) {
    return (
      <UpgradeNodeTierButton
        nodeTierId={currentTier.id}
        seats={seats}
        onUpgradeSuccess={onUpgradeSuccess}
      />
    )
  }

  return <MelddButton onPress={() => action('upgrade')} title={t('upgrade')} />
}

const CoreNodeTierCard = ({
  tier,
  config,
  currentTier,
  pendingTier,
  action,
  onCancelSuccess,
  onUpgradeSuccess,
  allowTierChanges = true,
  allowExtraSeats = true,
}: {
  tier: TierConfigDto
  config: NodeTierConfig
  currentTier?: {
    name: string
    seats: number
    id?: string
    endDate?: string
    status?: string
  }
  pendingTier?: {
    id: string
    name: string
    seats: number
    status: string
    startDate?: string | null
    endDate?: string | null
  }
  action: (
    action: 'start' | 'upgrade' | 'downgrade',
    {
      seats,
      name,
      basePrice,
    }: { seats: number; name: string; basePrice: number }
  ) => void
  onCancelSuccess?: () => void
  onUpgradeSuccess?: (
    billingId: string,
    amount: number,
    basePrice?: number
  ) => void
  allowTierChanges?: boolean
  allowExtraSeats?: boolean
}) => {
  const { t } = useTranslation('tiers')
  const seatUnitPrice = useMemo(() => {
    return tier.seat_price || 0
  }, [tier.seat_price])
  const initialSeatCount =
    currentTier?.seats && currentTier.seats > 0 ? currentTier.seats : 10
  const [selectedSeats, setSelectedSeats] = useState({
    seats: initialSeatCount,
    price: seatUnitPrice * initialSeatCount,
  })
  const [seatInput, setSeatInput] = useState(initialSeatCount.toString())
  const currency = useCurrency(selectedSeats.price)
  const extraSeatUnitPrice = useMemo(() => {
    if (tier.visibility === 'public') {
      return config.extra.extra_seat_public
    }
    return config.extra.extra_seat_private
  }, [tier.visibility, config.extra])
  const extraCurrency = useCurrency(extraSeatUnitPrice)

  useEffect(() => {
    const nextSeatCount =
      currentTier?.seats && currentTier.seats > 0 ? currentTier.seats : 10
    setSelectedSeats({
      seats: nextSeatCount,
      price: seatUnitPrice * nextSeatCount,
    })
    setSeatInput(nextSeatCount.toString())
  }, [currentTier?.seats, seatUnitPrice])

  return (
    <NodeTierCard
      title={t(tier.name.toLowerCase())}
      currentTier={currentTier}
      tierName={tier.name}
      selectedSeats={selectedSeats.seats}
      pendingTier={pendingTier}
      action={
        <Row style={{ flex: 1 }} justifyContent="space-between">
          <Text variant="titleMedium">
            {t('price')} {currency} {t('by-month')}
          </Text>
          <TierAction
            tier={tier}
            seats={selectedSeats.seats}
            currentTier={currentTier}
            pendingTier={pendingTier}
            action={(actionName) => {
              action(actionName, {
                seats: selectedSeats.seats,
                name: tier.name,
                basePrice: selectedSeats.price,
              })
            }}
            onCancelSuccess={onCancelSuccess}
            onUpgradeSuccess={onUpgradeSuccess}
            allowTierChanges={allowTierChanges}
          />
        </Row>
      }
    >
      <View style={{ gap: 8 }}>
        <Row
          justifyContent="space-between"
          style={{ flex: 1 }}
          alignItems="center"
        >
          <Row alignItems="center" columnGap={4}>
            <TouchableIconWithToolTip
              icon={<Icon source="seat" size={24} />}
              tooltip={t('core')}
            />
            <Text variant="titleMedium">{t('seats')}</Text>
          </Row>
          <Row alignItems="center" columnGap={4} style={{ paddingTop: 10 }}>
            <TextInput
              mode="outlined"
              value={seatInput}
              keyboardType="number-pad"
              maxLength={4}
              style={{ width: 72, height: 40 }}
              disabled={!allowTierChanges}
              onChangeText={(value) => {
                const digits = value.replace(/[^0-9]/g, '')
                setSeatInput(digits)
                const parsedSeats = Number(digits)
                if (!digits || parsedSeats <= 0) {
                  return
                }
                setSelectedSeats({
                  seats: parsedSeats,
                  price: seatUnitPrice * parsedSeats,
                })
              }}
            />
          </Row>
        </Row>
        {allowExtraSeats ? (
          <Text variant="bodySmall" style={{ opacity: 0.75 }}>
            {t('overflow-seat-price')}: {extraCurrency}
          </Text>
        ) : null}
      </View>
    </NodeTierCard>
  )
}

const FreeTierCard = ({
  canUseFreeTier,
  action,
}: {
  canUseFreeTier: boolean
  action: () => void
}) => {
  const { t } = useTranslation('tiers')
  return (
    <NodeTierCard
      title={t('trial')}
      action={
        <Row style={{ flex: 1 }} justifyContent="space-between">
          <Text variant="titleMedium">{t('trial-price')}</Text>
          <MelddButton
            isDisabled={!canUseFreeTier}
            onPress={() => {
              action()
            }}
            title={canUseFreeTier ? t('redeem') : t('redeem')}
          />
        </Row>
      }
    >
      <Text>{t('trial-description')}</Text>
    </NodeTierCard>
  )
}

const TierSummaryRow = ({
  currentTier,
  currentActiveUsers,
}: {
  currentTier?: {
    name: string
    seats: number
    id?: string
    endDate?: string
    status?: string
  }
  currentActiveUsers?: number | null
}) => {
  const { t } = useTranslation('tiers')
  const theme = useTheme()
  const styles = useStyle()

  if (!currentTier) {
    return null
  }

  const tierName = currentTier.name
  const seats =
    currentTier.seats === -1 ? t('unlimited') : currentTier.seats.toString()
  const activeUsers = currentActiveUsers ?? 0
  const endDate = currentTier.endDate
    ? moment(currentTier.endDate).format('MMMM DD, YYYY')
    : t('not-expires')
  const exceedsSeats =
    currentTier.seats !== -1 &&
    !!currentActiveUsers &&
    currentActiveUsers > currentTier.seats

  return (
    <View style={styles.summaryRow}>
      <Row justifyContent="space-between" alignItems="flex-start">
        <View style={{ flex: 1 }}>
          <Text variant="titleMedium">
            {t('selected-tier')}: {t(tierName.toLowerCase())}
          </Text>
          <Text variant="bodySmall" style={styles.summarySubtext}>
            {t('end-date')}
          </Text>
        </View>
        <View style={{ alignItems: 'flex-end' }}>
          {!!currentActiveUsers && (
            <Text
              variant="bodyMedium"
              style={[
                styles.summarySubtext,
                exceedsSeats && styles.exceedsSeatsText,
              ]}
            >
              {t('active-users')}: {activeUsers} / {t('seats')}: {seats}
            </Text>
          )}

          <Text variant="bodyMedium">{endDate}</Text>
        </View>
      </Row>
    </View>
  )
}

export const NodeTierList = ({
  visibility,
  currentTier,
  pendingTier,
  action,
  onCancelSuccess,
  onUpgradeSuccess,
  currentActiveUsers,
  allowTierChanges = true,
  allowExtraSeats = true,
}: {
  visibility: 'public' | 'private'
  currentTier?: {
    name: string
    seats: number
    id?: string
    endDate?: string
    status?: string
  }
  pendingTier?: {
    id: string
    name: string
    seats: number
    status: string
    startDate?: string | null
    endDate?: string | null
  }
  action: (
    action: 'start' | 'upgrade' | 'downgrade',
    {
      seats,
      name,
      basePrice,
    }: { seats: number; name: string; basePrice: number }
  ) => void
  onCancelSuccess?: () => void
  onUpgradeSuccess?: (
    billingId: string,
    amount: number,
    basePrice?: number
  ) => void
  currentActiveUsers?: number | null
  allowTierChanges?: boolean
  allowExtraSeats?: boolean
}) => {
  const theme = useTheme()
  const { data } = useGetNodeTierConfigQuery()
  const scrollViewRef = useRef<ScrollView>(null)
  const isMobile = useIsMobileDevice()
  const { t } = useTranslation('tiers')

  const tiers = useMemo(
    () =>
      data?.nodeTierConfig?.tiers.filter(
        (tier) => tier.layout === 'default' && tier.visibility === visibility
      ),
    [data, visibility]
  )
  const initialSelectedIndex = useMemo(() => {
    if (!currentTier) {
      return 1
    }
    return tiers?.findIndex((tier) => tier.name === currentTier?.name) || 1
  }, [currentTier, tiers, data?.nodeTierConfig])

  const scrollToIndex = (index: number) => {
    if (scrollViewRef.current) {
      // Calculate the position based on card width and spacing
      const cardWidth = Dimensions.get('window').width * 0.8
      const spacing = theme.spacing(4)
      const scrollPosition = index * (cardWidth + spacing)

      scrollViewRef.current.scrollTo({
        x: scrollPosition,
        animated: true,
      })
    }
  }

  // Scroll to initial selected index when component mounts
  useEffect(() => {
    if (initialSelectedIndex > 0) {
      // Add a small delay to ensure the ScrollView is properly rendered
      const timer = setTimeout(() => {
        scrollToIndex(initialSelectedIndex)
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [initialSelectedIndex])

  const cardStyle = useMemo(() => {
    return isMobile
      ? {
          width: Dimensions.get('window').width * 0.8,
          height: 500,
        }
      : { height: 500, flex: 1 }
  }, [isMobile])

  const handleContactUs = () => {
    const email = 'charles.q.du@meldd.com'
    const subject = t('enterprise-tier-inquiry')
    const body = t('enterprise-tier-inquiry-body')

    const mailtoUrl = `mailto:${email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`

    Linking.openURL(mailtoUrl).catch((err) => {
      console.error('Error opening mail app:', err)
    })
  }

  const tierComponents = useMemo(
    () =>
      data?.nodeTierConfig
        ? [
            ...(tiers?.map((tier) => (
              <View style={cardStyle} key={tier.name}>
                <CoreNodeTierCard
                  tier={tier}
                  currentTier={currentTier}
                  pendingTier={pendingTier}
                  config={data?.nodeTierConfig}
                  action={action}
                  onCancelSuccess={onCancelSuccess}
                  onUpgradeSuccess={onUpgradeSuccess}
                  allowTierChanges={allowTierChanges}
                  allowExtraSeats={allowExtraSeats}
                />
              </View>
            )) || []),
          ]
        : [],
    [
      tiers,
      currentTier,
      data?.nodeTierConfig,
      action,
      allowTierChanges,
      allowExtraSeats,
    ]
  )

  if (!data?.nodeTierConfig) {
    return null
  }

  return (
    <View>
      <TierSummaryRow
        currentTier={currentTier}
        currentActiveUsers={currentActiveUsers}
      />
      {!isMobile ? (
        <Row columnGap={theme.spacing(1)} justifyContent="space-between">
          {tierComponents.map((component, index) => (
            <View key={index} style={{ flex: 1 }}>
              {component}
            </View>
          ))}
        </Row>
      ) : (
        <ScrollView
          ref={scrollViewRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ columnGap: theme.spacing(2) }}
        >
          {tierComponents}
        </ScrollView>
      )}
      <Row
        style={{ flex: 1, paddingTop: theme.spacing(2) }}
        justifyContent="space-between"
      >
        <Text variant="titleMedium">{t('contact-us-label')}</Text>
        <MelddButton onPress={handleContactUs} title={t('contact-us')} />
      </Row>
    </View>
  )
}

const useStyle = createStyle(({ theme }) => ({
  cardPadding: {
    paddingHorizontal: theme.spacing(4),
    paddingVertical: theme.spacing(2),
  },
  renovationDateContainer: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTopWidth: 1,
    borderTopColor: theme.colors.outline,
  },
  renovationDateText: {
    color: theme.colors.onSurfaceVariant,
    fontStyle: 'italic',
  },
  cancelledMessageText: {
    color: theme.colors.error,
    fontStyle: 'italic',
  },
  summaryRow: {
    paddingVertical: theme.spacing(2),
    paddingHorizontal: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  summarySubtext: {
    color: theme.colors.onSurfaceVariant,
    marginTop: theme.spacing(0.5),
  },
  exceedsSeatsText: {
    color: theme.colors.error,
  },
}))
